import { useEffect, useState, lazy, Suspense, ComponentType } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { AuthProvider, useAuth } from "@/contexts/AuthContext"
import ProtectedRoute from "@/components/ProtectedRoute"
import AppErrorBoundary from "@/components/AppErrorBoundary"
import ErrorBoundary from "@/components/ErrorBoundary"
import { AdminImpersonationProvider } from "@/contexts/AdminImpersonationContext"
import { installGlobalErrorListeners } from "@/lib/systemLogger"
import { bootError, bootSetChunk, bootSetStage, hardReloadApp } from "@/lib/boot-debug"

installGlobalErrorListeners()

// Núcleo (eager) — rotas críticas no bundle inicial
import AppLayout from "@/components/AppLayout"
import LoginPage from "@/pages/Login"
import Dashboard from "@/pages/Dashboard"

// Helper: lazy com log de erro de carga de chunk
function lazyWithLog<T extends ComponentType<any>>(
  name: string,
  loader: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    {
      bootSetChunk(name, 'loading', 'Baixando chunk lazy')
      return loader()
        .then((mod) => {
          bootSetChunk(name, 'loaded', 'Chunk carregado com sucesso')
          return mod
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : String(err)
          bootSetChunk(name, 'failed', message)
          bootError(`Falha ao carregar chunk ${name}`, message)
          console.error(`[lazy] Falha ao carregar chunk "${name}":`, err)
          throw err
        })
    },
  )
}

// Lazy: páginas pós-login (não-críticas)
const DashboardObra = lazyWithLog('DashboardObra', () => import("@/pages/DashboardObra"))
const Obras = lazyWithLog('Obras', () => import("@/pages/Obras"))
const DiarioObra = lazyWithLog('DiarioObra', () => import("@/pages/DiarioObra"))
const Fotos = lazyWithLog('Fotos', () => import("@/pages/Fotos"))
const Ocorrencias = lazyWithLog('Ocorrencias', () => import("@/pages/Ocorrencias"))
const FVSPage = lazyWithLog('FVS', () => import("@/pages/FVS"))
const Pendencias = lazyWithLog('Pendencias', () => import("@/pages/Pendencias"))
const Producao = lazyWithLog('Producao', () => import("@/pages/Producao"))
const Cronograma = lazyWithLog('Cronograma', () => import("@/pages/Cronograma"))
const MapaVisual = lazyWithLog('MapaVisual', () => import("@/pages/MapaVisual"))
const Planta = lazyWithLog('Planta', () => import("@/pages/Planta"))
const Documentos = lazyWithLog('Documentos', () => import("@/pages/Documentos"))
const SST = lazyWithLog('SST', () => import("@/pages/SST"))
const APRPage = lazyWithLog('APR', () => import("@/pages/APR"))
const APTPage = lazyWithLog('APT', () => import("@/pages/APT"))
const PermissaoTrabalhoPage = lazyWithLog('PT', () => import("@/pages/PermissaoTrabalho"))
const DDSPage = lazyWithLog('DDS', () => import("@/pages/DDS"))
const AcidentesPage = lazyWithLog('Acidentes', () => import("@/pages/Acidentes"))
const InspecoesPage = lazyWithLog('Inspecoes', () => import("@/pages/Inspecoes"))
const Historico = lazyWithLog('Historico', () => import("@/pages/Historico"))
const EntregaUnidades = lazyWithLog('EntregaUnidades', () => import("@/pages/EntregaUnidades"))
const FVMPage = lazyWithLog('FVM', () => import("@/pages/FVM"))
const ConcretoPage = lazyWithLog('Concreto', () => import("@/pages/Concreto"))
const RevestimentosPage = lazyWithLog('Revestimentos', () => import("@/pages/Revestimentos"))
const EstoquePage = lazyWithLog('Estoque', () => import("@/pages/Estoque"))
const AssistenteIAPage = lazyWithLog('AssistenteIA', () => import("@/pages/AssistenteIA"))
const AssistenteEmBreve = lazyWithLog('AssistenteEmBreve', () => import("@/pages/AssistenteEmBreve"))
const NotFound = lazyWithLog('NotFound', () => import("@/pages/NotFound"))
const SolicitarAcessoPage = lazyWithLog('SolicitarAcesso', () => import("@/pages/SolicitarAcesso"))
const ForgotPasswordPage = lazyWithLog('ForgotPassword', () => import("@/pages/ForgotPassword"))
const ResetPasswordPage = lazyWithLog('ResetPassword', () => import("@/pages/ResetPassword"))
const AcceptInvitePage = lazyWithLog('AcceptInvite', () => import("@/pages/AcceptInvite"))
const TeamPage = lazyWithLog('Team', () => import("@/pages/Team"))
const AdminUsuarios = lazyWithLog('AdminUsuarios', () => import("@/pages/AdminUsuarios"))
const ChatPage = lazyWithLog('Chat', () => import("@/pages/Chat"))
const RelatoriosPage = lazyWithLog('Relatorios', () => import("@/pages/Relatorios"))
const RelatorioMensalPage = lazyWithLog('RelatorioMensal', () => import("@/pages/RelatorioMensal"))
const IdentidadeEmpresa = lazyWithLog('IdentidadeEmpresa', () => import("@/pages/IdentidadeEmpresa"))
const SettingsPage = lazyWithLog('Settings', () => import("@/pages/Settings"))
const SuperAdmin = lazyWithLog('SuperAdmin', () => import("@/pages/SuperAdmin"))
const OnboardingWizard = lazyWithLog('OnboardingWizard', () => import("@/components/OnboardingWizard"))
const AguardandoAprovacao = lazyWithLog('AguardandoAprovacao', () => import("@/pages/AguardandoAprovacao"))
const DebugPage = lazyWithLog('Debug', () => import("@/pages/Debug"))
const PortalCliente = lazyWithLog('PortalCliente', () => import("@/pages/PortalCliente"))
const PortalClienteAdmin = lazyWithLog('PortalClienteAdmin', () => import("@/pages/PortalClienteAdmin"))
const SystemLogsPage = lazyWithLog('SystemLogs', () => import("@/pages/admin/SystemLogsPage"))
const MobileApp = lazyWithLog('MobileApp', () => import("@/mobile/MobileApp"))

const queryClient = new QueryClient()

function SplashTimeoutFallback() {
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    bootSetStage('Montando app...', 'Aguardando carregamento do módulo atual')
    const timer = window.setTimeout(() => {
      setTimedOut(true)
      bootError('Timeout no Suspense', 'Um módulo demorou mais de 15s para carregar')
    }, 15000)

    return () => window.clearTimeout(timer)
  }, [])

  if (!timedOut) return <FullScreenLoader />

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-6 text-foreground">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
        <img src="/construzilla-logo.png?v=6" alt="Construzilla" className="mx-auto mb-3 h-auto w-24 object-contain" />
        <h2 className="text-lg font-extrabold">O app demorou para carregar</h2>
        <p className="mt-2 text-sm text-muted-foreground">Se algum chunk travou no mobile, abra <strong>/debug</strong> ou tente recarregar limpando o cache.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground" onClick={() => void hardReloadApp()} type="button">Tentar novamente</button>
          <button className="rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground" onClick={() => window.location.assign('/debug')} type="button">Abrir diagnóstico</button>
        </div>
      </div>
    </div>
  )
}

function FullScreenLoader() {
  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'hsl(var(--bg0))' }}>
      <div className="text-center animate-fade-up">
        <img src="/construzilla-logo.png?v=6" alt="Construzilla" className="inline-block object-contain mb-3" style={{ maxWidth: 120, background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 }} />
        <div className="text-foreground font-extrabold text-lg">Constru<span className="text-primary">zilla</span></div>
        <div className="mt-3">
          <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto" style={{ borderColor: 'hsl(var(--amber))', borderTopColor: 'transparent' }} />
        </div>
      </div>
    </div>
  )
}

function AuthGate() {
  const { user, loading, blocked, obras, company } = useAuth()
  const [onboardingDone, setOnboardingDone] = useState(false)

  useEffect(() => {
    if (loading) {
      bootSetStage('Iniciando autenticação...', 'Verificando sessão do usuário')
      return
    }

    if (!user) {
      bootSetStage('Exibindo login', 'Sessão não autenticada')
      return
    }

    if (user.status === 'pendente') {
      bootSetStage('Aguardando aprovação', 'Usuário autenticado, aguardando liberação')
      return
    }

    bootSetStage('App autenticado', 'Rotas protegidas liberadas')
  }, [loading, user])

  if (loading) return <FullScreenLoader />

  if (!user) {
    return (
      <Suspense fallback={<SplashTimeoutFallback />}>
        <Routes>
          <Route path="/solicitar-acesso" element={<SolicitarAcessoPage />} />
          <Route path="/aceitar-convite" element={<AcceptInvitePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/debug" element={<DebugPage />} />
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </Suspense>
    )
  }

  if (user.status === 'pendente') {
    return (
      <Suspense fallback={<SplashTimeoutFallback />}>
        <Routes>
          <Route path="*" element={<AguardandoAprovacao />} />
        </Routes>
      </Suspense>
    )
  }

  const needsOnboarding = !onboardingDone && company && obras.length === 0 && user.company_role === 'owner'
  if (needsOnboarding) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <OnboardingWizard onComplete={() => setOnboardingDone(true)} />
      </Suspense>
    )
  }

  return (
    <AppLayout>
      <ErrorBoundary module="route">
      <Suspense fallback={<SplashTimeoutFallback />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/debug" element={<DebugPage />} />
          <Route path="/obra" element={<DashboardObra />} />
          <Route path="/obras" element={<Obras />} />
          <Route path="/portal-admin/:obraId" element={<PortalClienteAdmin />} />
          <Route path="/diario" element={<DiarioObra />} />
          <Route path="/fotos" element={<Fotos />} />
          <Route path="/producao" element={<Producao />} />
          <Route path="/fvs" element={<FVSPage />} />
          <Route path="/pendencias" element={<Pendencias />} />
          <Route path="/ocorrencias" element={<Ocorrencias />} />
          <Route path="/planta" element={<Planta />} />
          <Route path="/documentos" element={<Documentos />} />
          <Route path="/cronograma" element={<Cronograma />} />
          <Route path="/mapa-visual" element={<MapaVisual />} />
          <Route path="/sst" element={<SST />} />
          <Route path="/apr" element={<APRPage />} />
          <Route path="/apt" element={<APTPage />} />
          <Route path="/pt" element={<PermissaoTrabalhoPage />} />
          <Route path="/dds" element={<DDSPage />} />
          <Route path="/acidentes" element={<AcidentesPage />} />
          <Route path="/inspecoes" element={<InspecoesPage />} />
          <Route path="/historico" element={<Historico />} />
          <Route path="/entregas" element={<EntregaUnidades />} />
          <Route path="/fvm" element={<FVMPage />} />
          <Route path="/concreto" element={<ConcretoPage />} />
          <Route path="/revestimentos" element={<RevestimentosPage />} />
          <Route path="/estoque" element={<EstoquePage />} />
          <Route path="/assistente-ia" element={<AssistenteIAPage />} />
          <Route path="/assistente" element={<AssistenteEmBreve />} />
          <Route path="/rdo" element={<Navigate to="/diario" replace />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/equipe" element={<TeamPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/relatorios" element={<RelatoriosPage />} />
          <Route path="/relatorio-mensal" element={<RelatorioMensalPage />} />
          <Route path="/admin/usuarios" element={<ProtectedRoute requireRole="admin"><AdminUsuarios /></ProtectedRoute>} />
          <Route path="/identidade" element={<ProtectedRoute requireRole="admin"><IdentidadeEmpresa /></ProtectedRoute>} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/superadmin" element={<ProtectedRoute requirePlatformAdmin><SuperAdmin /></ProtectedRoute>} />
          <Route path="/admin/system-logs" element={<ProtectedRoute requirePlatformAdmin><SystemLogsPage /></ProtectedRoute>} />
          <Route path="/admin/erros" element={<ProtectedRoute requirePlatformAdmin><SystemLogsPage /></ProtectedRoute>} />
          <Route path="/aceitar-convite" element={<AcceptInvitePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </AppLayout>
  )
}

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/portal/*" element={<Suspense fallback={<SplashTimeoutFallback />}><PortalCliente /></Suspense>} />
            <Route path="/mobile/*" element={<Suspense fallback={<SplashTimeoutFallback />}><MobileApp /></Suspense>} />
            <Route path="/debug" element={<Suspense fallback={<SplashTimeoutFallback />}><DebugPage /></Suspense>} />
            <Route path="*" element={<AuthProvider><AdminImpersonationProvider><AuthGate /></AdminImpersonationProvider></AuthProvider>} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
)

export default App
